import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';

import { combineLatest, iif, Observable, of, Subject } from 'rxjs';
import {
  combineLatestWith,
  concatMap,
  filter,
  map,
  switchMap,
  take,
  takeUntil,
} from 'rxjs/operators';

import { MatDialog } from '@angular/material/dialog';

import {
  ConsolidatedFeature,
  DcTypes,
  DeviceNameCommands,
  DeviceNameProperties,
  EmsKpiProperties,
  EmsPowerBalanceProperties,
  EmsPowerBalanceProperty,
  EmsPowerBalanceTypes,
  EmsPowerFlowsProperties,
  Feature,
  FeatureNames,
  FeaturePowerLimitationOverview,
  FeaturePropertyKey,
  HeatingRodHeatingCommands,
  HeatingRodHeatingProperties,
  HeatingRodStatusProperties,
  HemsConfigurationOverviewProperties,
  HemsType,
  IsNumberValueProperty,
  NumberValue,
  PhotovoltaicStatusProperties,
  PowerLimitationType,
  ThermalCockpitProperties,
} from '@vi-iot/client';

import { GTMService } from '@services';
import {
  DevicesStore,
  DeviceTypes,
  GatewayStore,
  Helpers,
  InstallationDetailsStore,
  InstallationFeaturesStore,
  RichDevice,
} from '@store';

import { HemsLiteInfoDialogComponent } from './hems-lite-info-dialog/hems-lite-info-dialog.component';
import { HemsFlowType } from '../../../@store/energy-historical-data/energy-historical-data.helpers';
import {
  EnergyFlowDirections,
  EnergyFlowInType,
  EnergyFlowOutType,
} from '../../../operational-data-common/translation-pipes/translate-energy-flow-type.pipe';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-energy-flow',
  templateUrl: './energy-flow.component.html',
  styleUrls: ['./energy-flow.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EnergyFlowComponent implements OnInit, OnDestroy {
  readonly FLOW_TYPES = HemsFlowType;
  readonly ENERGY_FLOW_IN_TYPE = EnergyFlowInType;
  readonly ENERGY_FLOW_OUT_TYPE = EnergyFlowOutType;
  readonly ENERGY_FLOW_DIRECTIONS = EnergyFlowDirections;
  readonly emsPowerBalanceTypes = EmsPowerBalanceTypes;
  readonly featurePropertyKey = FeaturePropertyKey;
  readonly possibleRunningRodStatusValues = ['1', '2', '3'];
  readonly PowerLimitationType = PowerLimitationType;
  readonly hemsType = HemsType;
  getHeatingRodStatusValue = Helpers.getHeatingRodStatusValue;
  stateOfCharge$: Observable<Feature<IsNumberValueProperty, never>>;
  stateOfChargeConsolidated$: Observable<Feature<ConsolidatedFeature, never>>;
  indicators$: Observable<Feature<EmsKpiProperties, never>>;
  powerBalance: Feature<EmsPowerBalanceProperties, never>;
  powerFlows$: Observable<Feature<EmsPowerFlowsProperties, never>>;
  pvStatus$: Observable<Feature<PhotovoltaicStatusProperties, never>>;
  heatingRodStatus$: Observable<Feature<HeatingRodStatusProperties, never>>;
  heatingSpfTotal$: Observable<Feature<IsNumberValueProperty, never>>;
  heatingRodHeating$: Observable<Feature<HeatingRodHeatingProperties, HeatingRodHeatingCommands>>;
  heatingRodDhw$: Observable<Feature<HeatingRodHeatingProperties, HeatingRodHeatingCommands>>;
  dryConcatPowerInstantaneousPower$: Observable<Feature<IsNumberValueProperty, never>>;
  emsPowerBalance$: Observable<Feature<EmsPowerBalanceProperties, never>>;
  powerBalanceTypeToValueMap = new Map<EmsPowerBalanceTypes, EmsPowerBalanceProperty>();
  dryContactgenericDevicePowerDetails: EmsPowerBalanceProperty[] = [];
  genericDeviceNames$: Observable<{ id: string; name: string }[]>;
  powerBalanceOrder = [
    EmsPowerBalanceTypes.Household,
    EmsPowerBalanceTypes.Heatpump,
    EmsPowerBalanceTypes.dryContactHeatpump,
    EmsPowerBalanceTypes.Accumulator,
    EmsPowerBalanceTypes.VCS,
    EmsPowerBalanceTypes.PhotovoltaicsHeater,
    EmsPowerBalanceTypes.dryContactGeneric,
    EmsPowerBalanceTypes.Grid,
  ];

  thermalCockpit$: Observable<Feature<ThermalCockpitProperties, never>>;
  consumptionLimitation$: Observable<FeaturePowerLimitationOverview>;
  productionLimitation$: Observable<FeaturePowerLimitationOverview>;
  activeHemsType$: Observable<Feature<HemsConfigurationOverviewProperties, never>>;

  isThermalFlow = false;

  private readonly otherDeviceFallbackNameTranslationKey =
    '+INSTALLATIONS.DRY_CONTACTS.GENERIC_CONSUMER.OTHER_DEVICE';
  private ngUnsubscribe$: Subject<void> = new Subject<void>();

  constructor(
    public gatewayStore: GatewayStore,
    public devicesStore: DevicesStore,
    private gtmService: GTMService,
    private cdr: ChangeDetectorRef,
    private installationFeaturesStore: InstallationFeaturesStore,
    private installationDetailsStore: InstallationDetailsStore,
    private translate: TranslateService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.thermalCockpit$ = this.installationFeaturesStore.selectInstallationGatewayFeature(
      FeatureNames.thermalCockpit
    );

    this.indicators$ = this.installationFeaturesStore.selectInstallationHemsFeature(
      FeatureNames.emsKeyPerformanceIndicators
    );
    this.heatingSpfTotal$ = this.installationFeaturesStore.selectInstallationDeviceFeatureForHeatingDevice(
      FeatureNames.heatingSpfTotal
    );
    this.heatingRodHeating$ = this.installationFeaturesStore.selectInstallationDeviceFeatureForHeatingDevice(
      FeatureNames.heatingRodHeating
    );
    this.heatingRodDhw$ = this.installationFeaturesStore.selectInstallationDeviceFeatureForHeatingDevice(
      FeatureNames.heatingRodDhw
    );
    this.heatingRodStatus$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.heatingRodStatus
    );
    this.consumptionLimitation$ = this.installationDetailsStore.getFeaturePowerLimitationOverview(
      PowerLimitationType.consumption
    );
    this.activeHemsType$ = this.installationFeaturesStore.selectInstallationGatewayFeature<
      HemsConfigurationOverviewProperties,
      never
    >(FeatureNames.hemsConfigurationOverview);
    this.productionLimitation$ = this.installationDetailsStore.getFeaturePowerLimitationOverview(
      PowerLimitationType.production
    );
    this.powerFlows$ = this.installationFeaturesStore.selectInstallationHemsFeature<
      EmsPowerFlowsProperties,
      never
    >(FeatureNames.emsPowerFlows);
    this.pvStatus$ = this.devicesStore.getPhotovoltaicDevice$.pipe(
      filter((device) => !!device),
      switchMap((device) =>
        this.installationFeaturesStore.selectInstallationDeviceFeature<
          PhotovoltaicStatusProperties,
          never
        >(FeatureNames.photovoltaicStatus, device.id)
      )
    );

    this.dryConcatPowerInstantaneousPower$ = this.devicesStore.dryContactHeatPump$.pipe(
      switchMap((device) => {
        if (device) {
          return this.installationFeaturesStore.selectInstallationDeviceFeature<
            IsNumberValueProperty,
            never
          >(FeatureNames.dryContactInstantaneousPower, device.id);
        } else return of(undefined);
      })
    );

    this.emsPowerBalance$ = this.installationFeaturesStore.selectInstallationHemsFeature<
      EmsPowerBalanceProperties,
      never
    >(FeatureNames.emsPowerBalance);

    this.genericDeviceNames$ = this.devicesStore.dryContactGeneric$.pipe(
      map((dryContactGenerics: RichDevice[]) => dryContactGenerics.map((device) => device.id)),
      switchMap((deviceIds: string[]) => {
        if (deviceIds.length === 0) {
          return of([]);
        }

        const devieNames$ = deviceIds.map((deivceId: string) =>
          this.installationFeaturesStore
            .selectInstallationDeviceFeature<DeviceNameProperties, DeviceNameCommands>(
              FeatureNames.deviceName,
              deivceId
            )
            .pipe(
              switchMap((feature) =>
                iif(
                  () => !!feature?.properties?.name?.value,
                  of({ id: deivceId, name: feature?.properties?.name?.value }),
                  of({
                    id: deivceId,
                    name: `${this.translate.instant(
                      this.otherDeviceFallbackNameTranslationKey
                    )} ${deivceId}`,
                  })
                )
              )
            )
        );

        return combineLatest(devieNames$);
      })
    );

    this.devicesStore.dryContactGeneric$
      .pipe(
        map((dryContactGenerics: RichDevice[]) => dryContactGenerics.map((device) => device.id)),
        switchMap((deviceIds: string[]) => {
          if (deviceIds.length === 0) {
            return of([]);
          }

          return combineLatest(
            deviceIds.map((deviceId) =>
              this.installationFeaturesStore.selectInstallationDeviceFeature<
                IsNumberValueProperty,
                never
              >(FeatureNames.dryContactInstantaneousPower, deviceId)
            )
          ).pipe(
            map((features) => {
              return features.filter((feature) => !!feature) as Feature<
                IsNumberValueProperty,
                never
              >[];
            })
          );
        }),
        combineLatestWith(
          this.dryConcatPowerInstantaneousPower$,
          this.emsPowerBalance$,
          this.genericDeviceNames$
        )
      )
      .subscribe(
        ([dryContactGenericValues, dryContactHeatPump, emsPowerBalance, genericDeviceNames]) => {
          if (dryContactGenericValues.length) {
            this.dryContactgenericDevicePowerDetails = [];
            dryContactGenericValues.forEach(
              (genericConsumptionDevice: Feature<IsNumberValueProperty, never>) => {
                this.dryContactgenericDevicePowerDetails.push({
                  type: EmsPowerBalanceTypes.dryContactGeneric,
                  name: genericDeviceNames.find(
                    (name) => name.id === genericConsumptionDevice.deviceId
                  )?.name,
                  value: genericConsumptionDevice.properties.value.value,
                  unit: genericConsumptionDevice.properties.value.unit,
                });
              }
            );
          }
          if (dryContactHeatPump) {
            this.powerBalanceTypeToValueMap.set(EmsPowerBalanceTypes.dryContactHeatpump, {
              type: EmsPowerBalanceTypes.dryContactHeatpump,
              value: dryContactHeatPump.properties.value.value,
              unit: dryContactHeatPump.properties.value.unit,
            });
          }
          if (emsPowerBalance) {
            emsPowerBalance.properties?.received.value.forEach((property) => {
              this.powerBalanceTypeToValueMap.set(property.type as EmsPowerBalanceTypes, {
                type: property.type as EmsPowerBalanceTypes,
                value: property.value,
                unit: property.unit,
              });
            });
          }
        }
      );

    this.setThermalFlowTab();

    // Stream set below in order to prevent .html from overcomplexity
    this.devicesStore.shouldUseConsolidatedFeature$
      .pipe(take(1), takeUntil(this.ngUnsubscribe$))
      .subscribe((shouldUseConsolidatedFeature) => {
        if (shouldUseConsolidatedFeature) {
          this.stateOfChargeConsolidated$ = this.installationFeaturesStore.selectInstallationDeviceFeatureByDeviceType(
            FeatureNames.stateOfChargeConsolidated,
            DeviceTypes.ElectricityStorage
          );
        } else {
          this.stateOfCharge$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
            FeatureNames.stateOfCapacityNominal
          );
        }
      });
  }

  openHemsLiteHelp(): void {
    this.dialog.open(HemsLiteInfoDialogComponent, {
      width: '560px',
      panelClass: 'hems-lite-info-dialog',
    });
  }

  onSelectFlowType(selectedFlowType: HemsFlowType): void {
    this.isThermalFlow = selectedFlowType === HemsFlowType.Thermal;
  }

  trackClose(): void {
    this.gtmService.track('onClose', 'Energy flows', 'energyFlows');
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }

  // transforming object to feature-like object to support value and unit pipe
  getPropertiesAsFeatureLike<T>(value: EmsPowerBalanceProperty): Feature<T, never> {
    const valueForPipeConsumption: NumberValue = {
      type: DcTypes.number, // EmsPowerBalanceTypes are not supported and number strategy will not pick it
      value: value.value,
      unit: value.unit,
    };

    return (<unknown>{
      properties: { value: valueForPipeConsumption },
    }) as Feature<T, never>;
  }

  private setThermalFlowTab(): void {
    this.installationFeaturesStore
      .selectInstallationHemsFeature<EmsPowerBalanceProperties, never>(FeatureNames.emsPowerBalance)
      .pipe(
        concatMap((powerBalance, requestIndex) => {
          if (requestIndex === 0) {
            this.isThermalFlow = !powerBalance;
          }

          return of(powerBalance);
        }),
        takeUntil(this.ngUnsubscribe$)
      )
      .subscribe((powerBalance) => {
        this.powerBalance = powerBalance;
        this.cdr.detectChanges();
      });
  }
}
